import { useRef } from 'react'

import { EMPTY_LIST } from '../../../../../constants/common'
import { isBrowserOffline } from '../../../../../utils/common'
import { GretaAIChatStatus } from '../../../GretaAIChat.constants'
import {
  getIsGretaAIChatFeedTurnInFlight,
  getIsGretaAIChatIdle,
  getIsGretaAIChatRetryableStatus,
  getIsGretaAIChatThinking,
} from '../../../GretaAIChat.utils'
import { getIsGretaAIChatNewChat } from '../../../GretaAIChat.utils.source'
import { appendGretaAIChatUserMessage } from '../../../GretaAIChatMessage'

import {
  getIsGretaAIChatLastTurnUser,
  withGretaAIChatRetryFlag,
} from './useGretaAIChatConversationTurns.utils'

export function useGretaAIChatConversationTurns({
  abortChatCreation,
  activeChatId,
  cancelQueuedTurn,
  cancelServerTurn,
  clearTimedOutTurn,
  createChatAndSend,
  getIsOpenConversation,
  getIsTimeoutStopPending,
  getIsTurnInFlight,
  messages,
  reloadMessages,
  requestReply,
  setMessages,
  setStatus,
  status,
  turnControllersRef,
  waitForPendingStop,
}) {
  // A retry of a chat that was never created must create it the way the first send asked.
  const creationOptionsRef = useRef(null)

  async function dispatchTurn(turnMessages, creationOptions) {
    // Check offline before sending: avoids a slow generic failure and, in a new
    // chat, an empty conversation the user never asked for.
    if (isBrowserOffline()) {
      setStatus(GretaAIChatStatus.Offline)

      return
    }

    setStatus(GretaAIChatStatus.Thinking)
    clearTimedOutTurn(activeChatId)

    const shouldSend = await waitForPendingStop(activeChatId)

    if (!shouldSend) {
      if (getIsOpenConversation(activeChatId)) {
        await reloadMessages(activeChatId)
      }
    } else if (activeChatId) {
      await requestReply(activeChatId, turnMessages)
    } else {
      await createChatAndSend(turnMessages, creationOptions)
    }
  }

  function getIsActiveChatTurnBlocked() {
    return getIsGretaAIChatFeedTurnInFlight(activeChatId, {
      getIsTimeoutStopPending,
      getIsTurnInFlight,
    })
  }

  function sendMessage(text, creationOptions) {
    const trimmedText = text.trim()

    if (
      trimmedText.length > 0 &&
      !getIsGretaAIChatThinking(status) &&
      !getIsActiveChatTurnBlocked()
    ) {
      const nextMessages = appendGretaAIChatUserMessage(messages, trimmedText)

      if (getIsGretaAIChatNewChat(activeChatId)) {
        creationOptionsRef.current = creationOptions
      }

      setMessages(nextMessages)
      dispatchTurn(nextMessages, creationOptions)

      return true
    }

    return false
  }

  function stopGeneration() {
    if (getIsGretaAIChatThinking(status) && !cancelQueuedTurn(activeChatId)) {
      const controller = turnControllersRef.current.get(activeChatId)

      controller?.abort()

      if (activeChatId === null) {
        abortChatCreation()
        setStatus(GretaAIChatStatus.Idle)
      } else {
        // Stays Thinking until the server confirms the stop — Idle here would let retry race it.
        cancelServerTurn(activeChatId, controller)
      }
    }
  }

  const canRetry =
    getIsGretaAIChatLastTurnUser(messages) &&
    (getIsGretaAIChatIdle(status) || getIsGretaAIChatRetryableStatus(status))

  const retryFlaggedMessages = canRetry
    ? withGretaAIChatRetryFlag(messages)
    : messages

  function retryLastReply() {
    if (!getIsActiveChatTurnBlocked()) {
      dispatchTurn(messages, creationOptionsRef.current)
    }
  }

  // A refused create leaves its question in the unsaved feed; a new chat drops it.
  function discardUnsavedChat() {
    if (
      getIsGretaAIChatNewChat(activeChatId) &&
      !getIsGretaAIChatThinking(status)
    ) {
      creationOptionsRef.current = null
      setMessages(EMPTY_LIST)
      setStatus(GretaAIChatStatus.Idle)
    }
  }

  return {
    discardUnsavedChat,
    retryFlaggedMessages,
    retryLastReply,
    sendMessage,
    stopGeneration,
  }
}
